/**
 * SyncManager.js - Core synchronization engine
 *
 * This is the core engine that handles all synchronization operations
 * including queue processing, inspection sync, and image compression.
 *
 * All operations run on the main thread with yield points to prevent UI blocking.
 */

import { db } from "@/services/db";
import convert from "@/services/convert";
import module_parser from "@/services/module_parser";
import store from "@/store";
import { Network } from "@capacitor/network";
import imageUploadService from "@/services/imageUploadService";
import uuid from "@/plugins/uuid";
import eventBus from "@/services/eventBus";

class SyncManager {
    constructor() {
        this.isRunning = false;
        this.currentTask = null;
        this.lastProgressUpdate = Date.now();
        this.callbacks = {};
        this.taskTimeoutId = null;
        this.platform = null;
        this.taskChunkSize = 100; // Default chunk size for batch operations
        this.maxAttempts = 3; // Maximum retry attempts for failed jobs

        // Rate limiting configuration
        this.requestDelay = 200; // Default delay between requests in ms
        this.rateLimitBackoff = {
            initialDelay: 1000, // Initial delay when 429 is encountered (1 second)
            maxDelay: 60000, // Maximum delay (60 seconds)
            multiplier: 2, // Exponential backoff multiplier
        };
        this.currentBackoffDelay = 0; // Current backoff delay
        this.rateLimitPauseUntil = 0; // Timestamp until which queue processing should be paused

        // Safety mechanism to prevent infinite loops
        this.processedJobIds = new Set(); // Track processed job IDs in current run
        this.maxJobProcessingAttempts = 5; // Max times to see the same job in one run

        // Failed job timeout configuration
        this.maxFailedJobAge = 4; // Hours before moving to permanently failed (default: 4 hours)
        // 4 hours gives enough time for temporary issues to resolve while not keeping jobs stuck too long
    }

    /**
     * Initialize the synchronization system
     * @param {Object} config - Optional configuration overrides
     */
    async init(config = {}) {
        // Apply configuration overrides
        if (config.maxFailedJobAge !== undefined) {
            this.maxFailedJobAge = config.maxFailedJobAge;
            console.log(`Failed job timeout set to ${this.maxFailedJobAge} hours`);
        }
        if (config.maxAttempts !== undefined) {
            this.maxAttempts = config.maxAttempts;
        }
        if (config.requestDelay !== undefined) {
            this.requestDelay = config.requestDelay;
        }

        // Detect platform for platform-specific optimizations
        this.platform = (await import("@capacitor/core")).Capacitor.getPlatform();

        // Set appropriate chunk size based on platform
        if (this.platform === "ios") {
            this.taskChunkSize = 50; // Smaller chunks for iOS
        } else if (this.platform === "android") {
            this.taskChunkSize = 100; // Medium chunks for Android
        } else {
            this.taskChunkSize = 200; // Larger chunks for web
        }

        console.log(`Sync manager initialized for ${this.platform} platform`);

        // Ensure database is ready
        await this.ensureDatabaseReady();

        // Process any pending jobs at startup if online
        if (store.getters.online && !store.getters.isQueueRunning) {
            this.checkForPendingJobs();
        }
    }

    /**
     * Check for pending jobs at startup
     */
    async checkForPendingJobs() {
        try {
            const jobCount = await db.jobs.count();
            if (jobCount > 0) {
                console.log(`Found ${jobCount} pending jobs at startup, triggering queue processing`);
                // Wait a short time to allow app to fully initialize
                setTimeout(() => {
                    // Don't wait for this to complete - fire and forget
                    this.startTask("process_queue", {}, {}).catch((err) => {
                        console.error("Error starting initial queue processing:", err);
                    });
                }, 3000);
            }
        } catch (error) {
            console.error("Error checking for pending jobs:", error);
        }
    }

    /**
     * Ensure database is ready and schema is fully migrated
     */
    async ensureDatabaseReady() {
        try {
            if (!db.isOpen()) {
                console.log("Database is not open, attempting to open it");
                await db.open();
            }
            return true;
        } catch (error) {
            console.error("Error ensuring database is ready:", error);
            return false;
        }
    }

    /**
     * Start a task with progress tracking
     *
     * @param {string} taskType - Type of task to start (process_queue, sync_inspection, etc.)
     * @param {object} params - Parameters for the task
     * @param {object} callbacks - Callbacks for task events (progress, complete, error, etc.)
     * @returns {boolean} - Whether the task was started successfully
     */
    async startTask(taskType, params = {}, callbacks = {}) {
        // Initialize if needed
        if (!this.platform) {
            await this.init();
        }

        // Don't run tasks if no bearer token (except setup_base_tables)
        if (!store.getters.getBearer && ["process_queue", "sync_inspection"].includes(taskType)) {
            console.log(`${taskType} not started: not logged in yet`);
            if (callbacks.error) {
                callbacks.error(new Error("Not logged in"));
            }
            return false;
        }

        // Check network status for network-dependent tasks
        if (["process_queue", "sync_inspection"].includes(taskType)) {
            const isOffline = !store.getters.online;
            const isForceSync = params?.forceSync === true;
            const ignoreOffline = params?.ignoreOffline === true;

            // Regular network checks for normal operations
            if (isOffline && !ignoreOffline && !isForceSync) {
                const errorMessage = `Cannot start ${taskType} while offline`;
                console.log(errorMessage);
                return false;
            }
        }

        // Check if another task is already running
        if (this.isRunning) {
            // If forceRestart is set, stop the current task
            if (params.forceRestart === true) {
                console.log(`Force restarting ${taskType}, stopping current task: ${this.currentTask?.type}`);
                this.stopTask("User requested force restart");
            } else {
                // Don't wait, just fail if another task is running
                const errorMessage = `Cannot start ${taskType} while ${this.currentTask?.type} is running`;
                console.log(errorMessage);
                return false;
            }
        }

        // Register callbacks
        this.callbacks = callbacks || {};

        // Add connection & device info to task params
        const enhancedParams = {
            ...params,
            api_url: store.getters.getApiUrl,
            bearer: store.getters.getBearer,
            platform: this.platform,
            connectionType: store.getters.getConnectionType,
            taskChunkSize: this.taskChunkSize,
            online: store.getters.online,
        };

        // Initialize task tracking
        this.isRunning = true;
        this.currentTask = {
            type: taskType,
            params: enhancedParams,
            startTime: Date.now(),
            progress: params.resumeProgress || 0,
        };

        this.lastProgressUpdate = Date.now();

        // Set up task timeout
        this.setupTaskTimeout();

        // Create a progress updater function
        const updateProgress = (progress, details) => {
            this.handleProgress(taskType, progress, details);
        };

        // Trigger 'start' callback
        this.triggerCallback("start", taskType, { params: enhancedParams });

        // Execute the appropriate task based on type
        try {
            if (taskType === "process_queue") {
                await this.processQueue(enhancedParams, updateProgress);
            } else if (taskType === "sync_inspection") {
                await this.syncInspection(enhancedParams, updateProgress);
            } else if (taskType === "setup_base_tables") {
                await this.setupBaseTables(enhancedParams, updateProgress);
            } else if (taskType === "compress_images") {
                await this.compressImages(enhancedParams, updateProgress);
            } else {
                throw new Error(`Unknown task type: ${taskType}`);
            }

            // Calculate duration before nullifying currentTask
            const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

            // Task completed successfully
            this.isRunning = false;
            this.currentTask = null;

            // Clear the timeout
            clearTimeout(this.taskTimeoutId);

            // Trigger completion callback
            this.triggerCallback("complete", taskType, {
                duration: duration,
            });

            return true;
        } catch (error) {
            // Calculate duration before nullifying currentTask
            const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

            // Task failed
            this.isRunning = false;
            this.currentTask = null;

            // Clear the timeout
            clearTimeout(this.taskTimeoutId);

            // Trigger error callback
            this.triggerCallback("error", taskType, {
                error: error.message,
                details: error.stack,
                duration: duration,
            });

            return false;
        }
    }

    /**
     * Process the job queue
     *
     * @param {object} params - Parameters for queue processing
     * @param {function} updateProgress - Function to update progress
     */
    async processQueue(params, updateProgress) {
        // Set initial progress
        updateProgress(0, "Starting queue processing");

        // Reset processed job tracking for this run
        this.processedJobIds.clear();

        try {
            // Check authentication - don't process if we're not logged in
            if (!params.bearer) {
                console.log("Queue skipped: not logged in yet");
                updateProgress(100, "Skipped - not logged in");
                return; // Silent success
            }

            // Check network status
            if (!params.online && !params.ignoreOffline) {
                throw new Error("Cannot process queue while offline");
            }

            // 1. First, send any permanently failed jobs to the server
            await this.runPermanentlyFailedJobs(params);
            updateProgress(10, "Processed permanently failed jobs");

            // 2. Process any existing failed jobs (retry up to max attempts)
            await this.runFailedJobs(params);
            updateProgress(20, "Processed failed jobs");

            // 3. Get all pending jobs from the database
            const jobs = await db.jobs.toArray();

            // Sort jobs based on priority
            const jobPriority = {
                post: 1,
                patch: 2,
                patcharray: 3,
                upload: 4,
                "upload-document": 5,
                delete: 6,
                "image-s3-upload": 8, // Create images first
                "image-s3-update": 9, // Update images after creates
                "image-confirm": 10,
            };

            // Module priority for proper dependency ordering
            // Lower numbers are processed first
            const modulePriority = {
                // Parent entities first
                inspections: 1,
                projects: 2,
                customers: 3,
                locations: 4,
                location_items: 5,

                // Components before their children
                location_item_components: 6,
                base_components: 6,

                // Dependent entities after parents
                measurements: 7,
                observations: 7,
                samples: 8,
                documents: 9,
                images: 10,

                // Form-related entities
                forms: 11,
                answers: 12,
                answers_inspections: 13,
                answers_location_item_components: 14,
                forms_location_item_components: 15,

                // System entities
                location_systems: 16,
                location_system_types: 17,

                // Other entities
                notifications: 18,
                scios_codes: 19,

                // Default for any unmapped modules
                default: 99,
            };

            const sortedJobs = jobs.sort((a, b) => {
                // First sort by job type priority
                const typeDiff = jobPriority[a.type.toLowerCase()] - jobPriority[b.type.toLowerCase()];
                if (typeDiff !== 0) return typeDiff;

                // For same type, sort by module priority to respect dependencies
                const aModulePriority = modulePriority[a.module] || modulePriority.default;
                const bModulePriority = modulePriority[b.module] || modulePriority.default;
                const moduleDiff = aModulePriority - bModulePriority;
                if (moduleDiff !== 0) return moduleDiff;

                // For same module, maintain original order (FIFO)
                return 0;
            });

            const totalJobs = sortedJobs.length;
            if (totalJobs === 0) {
                updateProgress(100, "No jobs to process");
                return;
            }

            // Count image jobs for notification
            const imageJobs = sortedJobs.filter(
                (job) =>
                    job.type === "image-s3-upload" || job.type === "image-s3-update" || job.type === "image-confirm"
            );

            if (imageJobs.length >= 2) {
                // Emit event for sync notification
                eventBus.emit("image-sync-started", {
                    count: imageJobs.length,
                });
                console.log(`Emitted image-sync-started event for ${imageJobs.length} images`);
            }

            // Process each job - use chunks to avoid blocking UI
            let processedCount = 0;

            // Divide jobs into chunks to improve UI responsiveness
            const chunks = this.chunkArray(sortedJobs, this.taskChunkSize);

            for (let i = 0; i < chunks.length; i++) {
                const jobChunk = chunks[i];

                // Process each job in the chunk
                for (const job of jobChunk) {
                    // Safety check: prevent processing the same job multiple times
                    const jobIdStr = String(job.id);
                    if (this.processedJobIds.has(jobIdStr)) {
                        console.warn(`Job ${job.id} already processed in this run, skipping to prevent loop`);
                        await this.toFailedJobs(job, new Error("Job stuck in processing loop"));
                        continue;
                    }
                    this.processedJobIds.add(jobIdStr);

                    // Check if we should pause due to rate limiting
                    if (this.rateLimitPauseUntil > Date.now()) {
                        const waitTime = this.rateLimitPauseUntil - Date.now();
                        updateProgress(
                            20 + Math.floor(80 * (processedCount / totalJobs)),
                            `Rate limited - waiting ${Math.ceil(waitTime / 1000)}s`
                        );
                        await new Promise((resolve) => setTimeout(resolve, waitTime));
                    }

                    try {
                        console.log(`Processing job ${job.id} of type ${job.type}`);
                        await this.processJob(job, params);
                        console.log(`Successfully processed job ${job.id}`);
                    } catch (error) {
                        console.error(`Job ${job.id} failed:`, {
                            type: job.type,
                            error: error.message,
                            stack: error.stack,
                        });
                        // Check if we're offline and this is a network error
                        if (error.isNetworkError && !params.online) {
                            console.log(`Job ${job.id} failed due to network error, stopping queue processing`);
                            // Put job back at the front of the queue
                            throw new Error("Network connection lost during queue processing");
                        }
                        await this.toFailedJobs(job, error);
                    }

                    processedCount++;

                    // Update progress
                    updateProgress(
                        20 + Math.floor(80 * (processedCount / totalJobs)),
                        `Processing job ${processedCount}/${totalJobs}: ${job.type}`
                    );

                    // Add delay between requests to avoid overwhelming the server
                    if (processedCount < totalJobs) {
                        await new Promise((resolve) => setTimeout(resolve, this.requestDelay));
                    }
                }

                // Yield to UI thread between chunks
                await new Promise((resolve) => setTimeout(resolve, 0));
            }

            // Clean up any floating records
            await this.deleteFloatingRecords();

            updateProgress(100, "Queue processing completed");
        } catch (error) {
            console.error("Error processing queue:", error);
            throw error;
        }
    }

    /**
     * Process a single job
     *
     * @param {object} job - The job to process
     * @param {object} params - Parameters for job processing
     */
    async processJob(job, params) {
        // Validate job structure - S3 upload jobs don't need URL
        const isS3Job = ["image-s3-upload", "image-s3-update", "image-confirm"].includes(job?.type);
        if (!job || !job.type || (!job.url && !isS3Job)) {
            console.error(`Invalid job structure:`, {
                id: job?.id,
                type: job?.type,
                url: job?.url,
                module: job?.module,
                payload: job?.payload,
            });
            await this.deleteJob(job);
            return;
        }

        // Check if job has exceeded max attempts
        const jobAttempt = parseInt(job.attempt || 0);
        const maxAttempts = isS3Job ? 3 : this.maxAttempts;
        if (jobAttempt >= maxAttempts) {
            console.error(
                `Job ${job.id} has already reached max attempts (${jobAttempt}/${maxAttempts}), moving to permanently failed`
            );
            const permanentJob = {
                ...job,
                error_message: `Exceeded max attempts (${maxAttempts})`,
                failed_at: new Date().toISOString(),
            };
            await db.permanently_failed_jobs.put(permanentJob);
            await this.deleteJob(job);
            return;
        }

        // Only process POST, PATCH, DELETE and upload jobs
        const validTypes = [
            "post",
            "patch",
            "patcharray",
            "delete",
            "upload",
            "upload-document",
            "image-s3-upload",
            "image-s3-update",
            "image-confirm",
        ];
        const jobType = job.type.toLowerCase();

        if (!validTypes.includes(jobType)) {
            console.warn(`Invalid job type: ${job.type}, removing from queue`);
            await this.deleteJob(job);
            return;
        }

        // Final URL validation - S3 upload jobs don't need URL
        if (!isS3Job && job.url && job.url.trim() === "") {
            console.error(`Empty URL for job ${job.id}, removing from queue`);
            await this.deleteJob(job);
            return;
        }

        // Determine HTTP method based on job.type
        let method;
        if (["upload", "upload-document"].includes(job.type)) {
            method = "POST";
        } else if (jobType === "patcharray") {
            method = "PATCH";
        } else {
            method = job.type.toUpperCase();
        }

        // Base options for fetch
        const options = {
            method,
            headers: {
                ...(params.bearer && { Authorization: `Bearer ${params.bearer}` }),
                Accept: "application/vnd.api+json",
            },
        };

        // Set body based on job.type
        if (["post", "patch", "patcharray"].includes(jobType)) {
            // Validate payload exists and is not empty
            if (!job.payload || (typeof job.payload === "object" && Object.keys(job.payload).length === 0)) {
                console.error(`Job ${job.id} (${job.type}) has empty or missing payload, removing from queue`);
                await this.deleteJob(job);
                return;
            }

            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(job.payload);
        } else if (["image-s3-upload", "image-s3-update", "image-confirm"].includes(job.type)) {
            // Handle new S3 upload job types - these are processed differently
            console.log(`Processing S3 upload job ${job.id}:`, {
                type: job.type,
                payload: job.payload,
            });

            try {
                if (job.type === "image-s3-upload") {
                    // Process S3 upload
                    if (!job.payload || !job.payload.image_uuid) {
                        throw new Error(`Invalid S3 upload job payload: ${JSON.stringify(job.payload)}`);
                    }

                    const image = await db.images.where("uuid").equals(job.payload.image_uuid).first();
                    if (!image) {
                        throw new Error(`Image not found in database: ${job.payload.image_uuid}`);
                    }

                    console.log(`Uploading image ${image.uuid} to S3 with bearer from params`);

                    // Initialize the service with bearer from params
                    imageUploadService.apiUrl = params.api_url;
                    imageUploadService.bearerToken = params.bearer;

                    console.log("Using bearer from params:", {
                        hasBearer: !!params.bearer,
                        bearerLength: params.bearer ? params.bearer.length : 0,
                    });

                    await imageUploadService.uploadImage(image, job.payload.entity_type, job.payload.imageable_uuid);

                    console.log(`Successfully uploaded image ${image.uuid}`);

                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "image-s3-update") {
                    // Process S3 image update (replace existing image)
                    if (!job.payload || !job.payload.existing_image_uuid) {
                        throw new Error(`Invalid S3 update job payload: ${JSON.stringify(job.payload)}`);
                    }

                    console.log(`Processing image update for ${job.payload.existing_image_uuid}`, {
                        entityType: job.payload.entity_type,
                        entityUuid: job.payload.imageable_uuid,
                    });

                    // Initialize the service with bearer from params
                    imageUploadService.apiUrl = params.api_url;
                    imageUploadService.bearerToken = params.bearer;

                    await imageUploadService.updateImage(
                        job.payload.existing_image_uuid,
                        job.payload.new_image_data,
                        job.payload.entity_type,
                        job.payload.imageable_uuid
                    );

                    console.log(`Successfully updated image ${job.payload.existing_image_uuid}`);
                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "image-confirm") {
                    // This is handled by uploadImage, but if we have a standalone confirm job
                    await imageUploadService.confirmUpload(
                        job.payload.upload_id,
                        job.payload.image_uuid,
                        job.payload.entity_type,
                        job.payload.entity_uuid,
                        job.payload.s3_key,
                        job.payload.title,
                        job.payload.orientation
                    );

                    await this.deleteJob(job);
                    return;
                }
            } catch (error) {
                console.error(`Error processing ${job.type} job:`, {
                    jobId: job.id,
                    error: error.message,
                    stack: error.stack,
                    payload: job.payload,
                });

                // Don't retry authentication errors - mark as permanently failed
                if (error.message && error.message.includes("401")) {
                    console.error("Authentication error - marking job as permanently failed");
                    await db.permanently_failed_jobs.put(job);
                    await this.deleteJob(job);
                    return;
                }

                throw error;
            }
        } else if (["upload", "upload-document"].includes(job.type)) {
            try {
                const formData = convert.toFormData(job.payload);

                if (job.type === "upload") {
                    formData.append("image", job.image);
                    formData.append("image_uuid", job.image_uuid);
                    formData.append("orientation", job.orientation);
                } else if (job.type === "upload-document") {
                    formData.append("file", job.file);
                }

                options.body = formData;
            } catch (formDataError) {
                console.error(`Error creating FormData for ${job.type} job:`, {
                    jobId: job.id,
                    error: formDataError.message,
                    type: job.type,
                });
                // For image upload errors, we typically want to fail the job rather than retry
                throw new Error(`FormData error: ${formDataError.message}`);
            }
        }

        // Add timeout for the fetch operation
        const controller = new AbortController();
        options.signal = controller.signal;
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for uploads

        try {
            // Execute the call
            const response = await fetch(`${params.api_url}${job.url}`, options);
            clearTimeout(timeoutId);

            if (!response.ok) {
                // Try to get error details from response
                let errorDetails = "";
                try {
                    const errorBody = await response.text();
                    if (errorBody) {
                        errorDetails = ` - ${errorBody.substring(0, 200)}`; // Limit error message length
                    }
                } catch (e) {
                    // Ignore errors reading response body
                }

                const error = new Error(`Server error: ${response.status}${errorDetails}`);
                error.response = response;
                error.status = response.status;

                // Log detailed error information
                console.error(`Job ${job.id} failed:`, {
                    type: job.type,
                    url: job.url,
                    status: response.status,
                    error: errorDetails,
                    module: job.module,
                });

                // Handle rate limiting (429)
                if (response.status === 429) {
                    // Calculate exponential backoff
                    if (this.currentBackoffDelay === 0) {
                        this.currentBackoffDelay = this.rateLimitBackoff.initialDelay;
                    } else {
                        this.currentBackoffDelay = Math.min(
                            this.currentBackoffDelay * this.rateLimitBackoff.multiplier,
                            this.rateLimitBackoff.maxDelay
                        );
                    }

                    // Set pause until timestamp
                    this.rateLimitPauseUntil = Date.now() + this.currentBackoffDelay;

                    console.warn(`Rate limited (429). Pausing queue for ${this.currentBackoffDelay}ms`);
                }

                throw error;
            }

            // Reset backoff on successful request
            if (this.currentBackoffDelay > 0) {
                this.currentBackoffDelay = 0;
            }

            // Only if it's not a delete or upload action, store the response in Dexie
            // Skip storing response for bulk operations as data is already stored locally
            if (!["delete", "upload", "upload-document"].includes(job.type) && !job.url.includes("/bulk-")) {
                try {
                    const responseData = await response.json();

                    if (Array.isArray(responseData)) {
                        await db[job.module].bulkPut(responseData);
                    } else if (responseData && Object.keys(responseData).length > 0) {
                        await db[job.module].put(responseData);
                    }
                } catch (jsonError) {
                    console.warn(`Error parsing JSON response for job ${job.id}:`, {
                        error: jsonError.message,
                        type: job.type,
                        url: job.url,
                    });
                    // Continue with job deletion since the server accepted it
                }
            }

            // Delete job from jobs store
            await this.deleteJob(job);
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === "AbortError") {
                throw new Error(`Fetch timeout for job ${job.id}`);
            }

            // Handle network errors differently from server errors
            if (error.name === "TypeError" && error.message.includes("fetch")) {
                // Network error - likely offline or connection issue
                console.warn(`Network error for job ${job.id}, will retry when connection is restored`);
                error.isNetworkError = true;
            }

            throw error;
        }
    }

    /**
     * Process failed jobs by retrying or moving to permanently failed
     */
    async runFailedJobs(params) {
        try {
            const failed_jobs = await db.failed_jobs.toArray();

            for (const failed_job of failed_jobs) {
                // Skip if job is marked as permanent failure
                if (failed_job.permanent) {
                    console.log(`Job ${failed_job.id} is marked as permanent failure, moving to permanently failed`);
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    continue;
                }

                // Check if this is an orphaned image job (image doesn't exist)
                if (failed_job.type === "image-s3-upload" && failed_job.payload?.image_uuid) {
                    const image = await db.images.get(failed_job.payload.image_uuid);
                    if (!image) {
                        console.log(
                            `Image ${failed_job.payload.image_uuid} not found, permanently failing job ${failed_job.id}`
                        );
                        const permJob = {
                            ...failed_job,
                            permanent: true,
                            error_message: "Image not found in database",
                        };
                        await db.permanently_failed_jobs.put(permJob);
                        await db.failed_jobs.delete(failed_job.id);
                        continue;
                    }
                }

                // Check if job has been failed for too long
                const failedAt = failed_job.failed_at ? new Date(failed_job.failed_at) : new Date();
                const hoursInFailedState = (Date.now() - failedAt.getTime()) / (1000 * 60 * 60);

                // If job has been in failed state for longer than maxFailedJobAge, move to permanently failed
                if (hoursInFailedState > this.maxFailedJobAge) {
                    console.warn(
                        `Job ${failed_job.id} has been failed for ${hoursInFailedState.toFixed(1)} hours (max: ${
                            this.maxFailedJobAge
                        }), moving to permanently failed`
                    );
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    await this.postPermanentFailedJob(failed_job, params);
                    continue;
                }

                // Only retry POST, PATCH, DELETE jobs and image uploads
                const jobType = failed_job.type.toLowerCase();
                const canRetry = ["post", "patch", "patcharray", "delete", "image-s3-upload", "image-confirm"].includes(
                    jobType
                );

                // Special handling for image upload jobs - limit to 3 retries
                const maxAttempts = jobType.includes("image") ? 3 : this.maxAttempts;

                // Make sure attempt is a number
                const currentAttempt = parseInt(failed_job.attempt || 0);

                if (canRetry && currentAttempt < maxAttempts) {
                    // Skip retry if it's a CORS error
                    if (failed_job.cors_error || failed_job.error_message?.includes("CORS")) {
                        console.log(`Job ${failed_job.id} has CORS error, not retrying`);
                        await db.permanently_failed_jobs.put(failed_job);
                        await db.failed_jobs.delete(failed_job.id);
                        await this.postPermanentFailedJob(failed_job, params);
                    } else {
                        // Retry the job - preserve attempt count
                        console.log(`Retrying job ${failed_job.id}, attempt ${currentAttempt + 1}/${maxAttempts}`);
                        const retryJob = {
                            ...failed_job,
                            attempt: currentAttempt, // Keep the current attempt count
                        };
                        delete retryJob.error_message; // Remove error message for retry
                        delete retryJob.failed_at; // Remove failed timestamp

                        await db.jobs.put(retryJob);
                        await db.failed_jobs.delete(failed_job.id);
                    }
                } else {
                    // Move to permanently failed
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    // Send the permanently failed job to the server
                    await this.postPermanentFailedJob(failed_job, params);
                }
            }
        } catch (error) {
            console.error("Error in runFailedJobs:", error);
        }
    }

    /**
     * Process permanently failed jobs by sending to server
     */
    async runPermanentlyFailedJobs(params) {
        try {
            const perm_failed_jobs = await db.permanently_failed_jobs.toArray();

            for (const failed_job of perm_failed_jobs) {
                await this.postPermanentFailedJob(failed_job, params);
            }
        } catch (error) {
            console.error("Error in runPermanentlyFailedJobs:", error);
        }
    }

    /**
     * Move a job to the failed_jobs store
     */
    async toFailedJobs(job, error) {
        try {
            // For rate limiting errors, put the job back in queue without incrementing attempts
            if (error?.status === 429 || error?.response?.status === 429) {
                console.log(`Job ${job.id} rate limited, will retry after cooldown`);
                // Job stays in the queue, will be retried after the rate limit pause
                return;
            }

            // Check for CORS errors specifically
            if (
                error?.isCorsError ||
                error?.message?.includes("CORS_ERROR") ||
                (error?.message?.includes("CORS") && job?.type === "image-s3-upload")
            ) {
                console.error(`Job ${job.id} failed due to CORS configuration error - not retrying`);

                // Move directly to permanently failed - CORS won't fix itself with retries
                const permanentlyFailedJob = {
                    ...job,
                    error_message:
                        "S3 bucket CORS configuration error - POST method not allowed. Contact administrator.",
                    attempt: (job.attempt || 0) + 1,
                    failed_at: new Date().toISOString(),
                    cors_error: true,
                };

                await db.permanently_failed_jobs.put(permanentlyFailedJob);
                await this.deleteJob(job);

                // Update image status if this is an image job
                if (job.payload?.image_uuid) {
                    await db.images.update(job.payload.image_uuid, {
                        upload_status: "cors_error",
                        upload_error: "S3 bucket needs CORS configuration for POST method",
                    });
                }

                return;
            }

            // Check for S3 Policy errors (wrong Content-Type, etc)
            if (
                error?.isPolicyError ||
                error?.message?.includes("Policy Error") ||
                error?.message?.includes("Policy Condition failed") ||
                error?.message?.includes("Invalid according to Policy")
            ) {
                console.error(`Job ${job.id} failed due to S3 Policy error - not retrying`);

                // Move directly to permanently failed - Policy errors need backend/format fixes
                const permanentlyFailedJob = {
                    ...job,
                    error_message: error.message.substring(0, 200),
                    attempt: (job.attempt || 0) + 1,
                    failed_at: new Date().toISOString(),
                    policy_error: true,
                    permanent: true,
                };

                await db.permanently_failed_jobs.put(permanentlyFailedJob);
                await this.deleteJob(job);

                // Update image status if this is an image job
                if (job.payload?.image_uuid) {
                    await db.images.update(job.payload.image_uuid, {
                        upload_status: "policy_error",
                        upload_error: "S3 Policy error - check image format",
                    });
                }

                return;
            }

            // Check for ANY 500 errors - these are server errors that won't be fixed by retrying
            if (error?.message?.includes("500") || error?.status === 500 || error?.response?.status === 500) {
                console.error(`Job ${job.id} failed with server error (500) - not retrying`);

                // Determine specific error message
                let errorMessage = "Server error (500)";
                let imageError = "Server error - please try again later or contact support";

                if (error?.message?.includes("Entity not found")) {
                    errorMessage = "Entity not found on server";
                    imageError = "Entity not found - measurement/observation may have been deleted";
                } else if (error?.message?.includes("Failed to prepare upload")) {
                    errorMessage = "Failed to prepare upload - server error";
                    imageError = "Server failed to prepare upload";
                }

                // Move directly to permanently failed - Server errors shouldn't be retried
                const permanentlyFailedJob = {
                    ...job,
                    error_message: `${errorMessage}: ${error.message.substring(0, 200)}`,
                    attempt: (job.attempt || 0) + 1,
                    failed_at: new Date().toISOString(),
                    server_error: true,
                    permanent: true,
                };

                await db.permanently_failed_jobs.put(permanentlyFailedJob);
                await this.deleteJob(job);

                // Update image status if this is an image job
                if (job.payload?.image_uuid) {
                    try {
                        await db.images.update(job.payload.image_uuid, {
                            upload_status: "failed",
                            upload_error: imageError,
                        });
                    } catch (e) {
                        console.log(`Could not update image ${job.payload.image_uuid}`);
                    }
                }

                return;
            }

            // Initialize attempt if not present
            if (job.attempt === undefined || job.attempt === null) {
                job.attempt = 0;
            }

            const attemptCount = job.attempt + 1;

            // Check max attempts for image jobs - limit to 3
            if (job.type === "image-s3-upload" || job.type === "image-confirm") {
                if (attemptCount >= 3) {
                    console.error(`Image upload job ${job.id} exceeded max attempts (3), moving to permanently failed`);

                    // First delete from all possible locations to prevent duplication
                    await this.deleteJob(job);
                    if (job.id) {
                        await db.failed_jobs.where("id").equals(job.id).delete();
                    }

                    const permanentlyFailedJob = {
                        ...job,
                        id: job.id || Date.now() + Math.random(), // Ensure unique ID
                        error_message: `Failed after ${attemptCount} attempts: ${error.message}`,
                        attempt: attemptCount,
                        failed_at: new Date().toISOString(),
                        permanent: true, // Mark as permanent to prevent re-processing
                    };

                    await db.permanently_failed_jobs.put(permanentlyFailedJob);

                    // Update image status if we can find it
                    if (job.payload?.image_uuid) {
                        try {
                            const image = await db.images.get(job.payload.image_uuid);
                            if (image) {
                                await db.images.update(job.payload.image_uuid, {
                                    upload_status: "failed",
                                    upload_error: `Upload failed after ${attemptCount} attempts - Image may not exist`,
                                });
                            }
                        } catch (e) {
                            console.log(`Image ${job.payload.image_uuid} not found in database`);
                        }
                    }
                    return;
                }
            }

            const cloned_job = {
                ...job,
                error_message: error.message,
                attempt: attemptCount,
                failed_at: new Date().toISOString(),
            };

            // Handle 404 errors more intelligently
            if (error?.response?.status === 404) {
                // For image uploads, check if parent observation/measurement exists locally
                if (job.type === "upload" && job.url && job.url.includes("/images")) {
                    // Extract parent UUID from URL (e.g., /observations/uuid/images)
                    const urlParts = job.url.split("/");
                    let parentIndex = urlParts.indexOf("observations");
                    if (parentIndex === -1) {
                        parentIndex = urlParts.indexOf("measurements");
                    }

                    if (parentIndex !== -1 && urlParts[parentIndex + 1]) {
                        const parentUuid = urlParts[parentIndex + 1];
                        const parentModule = urlParts[parentIndex];

                        // Check if parent exists in queue or locally
                        const pendingParentJob = await db.jobs.where("payload.uuid").equals(parentUuid).first();

                        const failedParentJob = await db.failed_jobs.where("payload.uuid").equals(parentUuid).first();

                        if (pendingParentJob || failedParentJob) {
                            // Parent is still pending or failed, retry this job later
                            console.log(
                                `Image upload job ${job.id} failed with 404, but parent ${parentUuid} is still pending/failed. Will retry.`
                            );
                            cloned_job.error_message = `Parent resource ${parentUuid} not yet synced`;

                            // Check if already in failed_jobs to prevent duplicates
                            const existingFailed = await db.failed_jobs.where("uuid").equals(job.uuid).first();
                            if (!existingFailed) {
                                await db.failed_jobs.put(cloned_job);
                            }
                            await this.deleteJob(job);
                            return;
                        }
                    }
                }

                // Otherwise, assume the resource no longer exists and remove the job
                console.log(`Job ${job.id} failed with 404, removing from queue`);
                await this.deleteJob(job);
                return;
            }

            // Check if already in failed_jobs to prevent duplicates
            const existingFailed = await db.failed_jobs.where("uuid").equals(job.uuid).first();
            if (!existingFailed) {
                await db.failed_jobs.put(cloned_job);
            }
            await this.deleteJob(job);
        } catch (err) {
            console.error(`Error in toFailedJobs for job ${job.id}:`, err);
        }
    }

    /**
     * Delete a job from the jobs store
     */
    async deleteJob(job) {
        try {
            await db.jobs.delete(job.id);
        } catch (err) {
            console.error(`Error deleting job ${job.id}:`, err);
        }
    }

    /**
     * Post permanently failed job to the server
     */
    async postPermanentFailedJob(job, params) {
        try {
            if (!params.online && !params.ignoreOffline) {
                return;
            }

            // Create a sanitized copy of the job to send to backend
            const jobToSend = { ...job };

            // For S3 update jobs, remove the heavy base64 data from payload
            if (job.type === "image-s3-update" && job.payload) {
                jobToSend.payload = { ...job.payload };

                // Remove the base64 image data (can be several MB)
                if (jobToSend.payload.new_image_data) {
                    delete jobToSend.payload.new_image_data;
                }

                // Try to include S3 key if the image exists
                if (job.payload.existing_image_uuid) {
                    const image = await db.images.get(job.payload.existing_image_uuid);
                    if (image && image.s3_key) {
                        jobToSend.s3_key = image.s3_key;
                    }
                }
            }

            // For S3 upload jobs, include S3 key if the upload partially succeeded
            if (job.type === "image-s3-upload" && job.payload && job.payload.image_uuid) {
                const image = await db.images.get(job.payload.image_uuid);
                if (image && image.s3_key) {
                    jobToSend.s3_key = image.s3_key;
                }
            }

            // Set a timeout for the fetch operation
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await fetch(`${params.api_url}/failed-jobs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/vnd.api+json",
                    ...(params.bearer && { Authorization: `Bearer ${params.bearer}` }),
                },
                body: JSON.stringify({ data: jobToSend }),
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = new Error(`Server error: ${response.status}`);
                error.response = response;
                throw error;
            }

            // Delete the permanently failed job if the server received it correctly
            await db.permanently_failed_jobs.delete(job.id);
        } catch (error) {
            console.error("Permanently failed job error:", error);
            // Don't rethrow - we want to continue processing other jobs
        }
    }

    /**
     * Clean up floating records in the database
     */
    async deleteFloatingRecords() {
        const all_lics = await db.location_item_components.toArray();
        const all_lic_uuids = all_lics.map((lic) => lic.uuid);
        const all_floating_records = await db.location_item_components
            .where("parent_uuid")
            .noneOf(all_lic_uuids)
            .toArray();

        await db.location_item_components.bulkDelete(all_floating_records.map((lic) => lic.uuid));
    }

    /**
     * Merge attribute_values from optimized API with base component schema
     * @param {Object} locationItemComponent - Component with attribute_values
     * @returns {Object} Component with full attributes.fields
     */
    async mergeComponentAttributes(locationItemComponent) {
        // If no attribute_values, initialize empty
        if (!locationItemComponent.attribute_values) {
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        // Load base component schema
        const baseComponent = await db.base_components.where({ uuid: locationItemComponent.component_uuid }).first();

        if (!baseComponent) {
            console.warn(`Base component not found in database for UUID: ${locationItemComponent.component_uuid}`);
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        if (!baseComponent.attributes || !baseComponent.attributes.fields) {
            // Component has no attributes schema defined - this is OK for simple components
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        // Create lookup map for saved values
        const valuesMap = {};
        locationItemComponent.attribute_values.forEach((item) => {
            valuesMap[item.uuid] = item.value;
        });

        // Merge: Start with schema, apply saved values
        const mergedFields = baseComponent.attributes.fields.map((field) => {
            return {
                ...field, // Full schema (label, options, type, rules, placeholder, etc.)
                value: valuesMap[field.uuid] !== undefined ? valuesMap[field.uuid] : field.value || null,
            };
        });

        // Set merged attributes and clean up temporary field
        locationItemComponent.attributes = {
            fields: mergedFields,
        };

        delete locationItemComponent.attribute_values; // Remove temporary field

        return locationItemComponent;
    }

    /**
     * Synchronize an inspection
     */
    async syncInspection(params, updateProgress) {
        const { api_url, bearer, inspection_id, online } = params;

        // Check network status - don't proceed if offline
        if (!online && !params.ignoreOffline) {
            throw new Error("Cannot sync inspection while offline");
        }

        if (!inspection_id || typeof inspection_id !== "number") {
            console.error("Invalid inspection_id received:", inspection_id, "Type:", typeof inspection_id);
            throw new Error(
                `Invalid inspection_id received: ${JSON.stringify(inspection_id)} (type: ${typeof inspection_id})`
            );
        }

        if (!api_url || !bearer) {
            throw new Error("Missing API configuration for inspection sync");
        }

        updateProgress(0, "Starting inspection sync");

        // Fetch inspection data
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
            const response = await fetch(`${api_url}/inspections/${inspection_id}/optimized`, {
                headers: {
                    Authorization: `Bearer ${bearer}`,
                    Accept: "application/vnd.api+json",
                    ContentType: "application/vnd.api+json",
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Failed to fetch inspection data: ${response.status}`);
            }

            const inspection = await response.json();
            updateProgress(10, `Syncing inspection: ${inspection.name}`);

            // Process inspection data with chunking for better UI responsiveness
            await this.processInspectionData(inspection, (progress, detail) => {
                // Scale progress from 10-100%
                updateProgress(10 + progress * 0.9, detail);
            });

            updateProgress(100, "Inspection sync complete");
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === "AbortError") {
                throw new Error("Inspection data fetch timed out");
            }

            throw error;
        }
    }

    /**
     * Process inspection data
     */
    async processInspectionData(inspection, progressCallback) {
        let nonImageTasks = [];
        let imageTasks = [];

        // Build task lists for different parts of the inspection data
        // Samples
        if (inspection.samples) {
            nonImageTasks.push(async () => {
                await this.add("samples", inspection.samples, { inspection_id: inspection.id });
            });
        }

        // SCIOS codes
        if (inspection.scios_codes) {
            nonImageTasks.push(async () => {
                await this.add("scios_codes", inspection.scios_codes, { inspection_id: inspection.id });
            });
        }

        // Location systems
        if (inspection.location_systems) {
            nonImageTasks.push(async () => {
                await Promise.all(
                    inspection.location_systems.map((ls) =>
                        this.add("location_systems", ls, { location_systemable_uuid: inspection.location.uuid })
                    )
                );
            });
        }

        // Project
        if (inspection.project) {
            nonImageTasks.push(async () => {
                inspection.project.inspection_id = inspection.id;
                await this.add("projects", [inspection.project], { inspection_id: inspection.id });
            });
        }

        // Customer
        if (inspection.customer) {
            nonImageTasks.push(async () => {
                inspection.customer.inspection_id = inspection.id;
                await this.add("customers", [inspection.customer], { inspection_id: inspection.id });
            });
        }

        // Customer contact
        if (inspection.customer_contact && inspection.customer?.uuid) {
            nonImageTasks.push(async () => {
                const customer = await db.customers.get({ uuid: inspection.customer.uuid });
                if (customer) {
                    customer.customer_contact = inspection.customer_contact;
                    await db.customers.put(customer);
                }
            });
        }

        // Inspector users
        if (inspection.inspector_users) {
            nonImageTasks.push(async () => {
                inspection.inspector_users.forEach((user) => {
                    user.inspection_id = inspection.id;
                    user.id = `${inspection.id}-${user.id}`;
                });
                await this.add(
                    "inspector_users",
                    inspection.inspector_users,
                    { inspection_id: inspection.id },
                    "inspection_id"
                );
            });
        }

        // Location (without location_items)
        if (inspection.location) {
            nonImageTasks.push(async () => {
                const location = { ...inspection.location, inspection_id: inspection.id };
                delete location.location_items;
                await this.add("locations", [location], { inspection_id: inspection.id });

                if (inspection.location.location_systems) {
                    await this.add("location_systems", inspection.location.location_systems, {
                        location_systemable_uuid: inspection.location.uuid,
                    });
                }
            });

            // Add location images to imageTasks
            if (inspection.location.images) {
                imageTasks.push(async () => {
                    await this.add("images", inspection.location.images, { imageable_uuid: inspection.location.uuid });
                });
            }
        }

        // Documents
        if (inspection.documents) {
            nonImageTasks.push(async () => {
                inspection.documents.forEach((doc) => {
                    doc.documentable_uuid = inspection.uuid;
                });
                await this.add("documents", inspection.documents, { documentable_uuid: inspection.uuid });
            });
        }

        // Location items and components
        if (inspection.location && inspection.location.location_items) {
            const locationItems = inspection.location.location_items;

            // First process components for each location item
            for (const locationItem of locationItems) {
                nonImageTasks.push(async () => {
                    if (locationItem.location_item_components) {
                        // Merge attribute_values with base component schemas
                        const mergedComponents = [];
                        for (const component of locationItem.location_item_components) {
                            const merged = await this.mergeComponentAttributes(component);
                            mergedComponents.push(merged);
                        }

                        // Store merged components
                        await this.add("location_item_components", mergedComponents, {
                            location_item_uuid: locationItem.uuid,
                        });

                        // Process nested data (images, observations, measurements, etc.)
                        for (const component of mergedComponents) {
                            // Component images
                            if (component.images) {
                                imageTasks.push(async () => {
                                    await this.add("images", component.images, { imageable_uuid: component.uuid });
                                });
                            }

                            // Observations
                            if (component.observations) {
                                await this.add("observations", component.observations, {
                                    location_item_component_uuid: component.uuid,
                                });

                                for (const observation of component.observations) {
                                    if (observation.images) {
                                        imageTasks.push(async () => {
                                            await this.add("images", observation.images, {
                                                imageable_uuid: observation.uuid,
                                            });
                                        });
                                    }
                                }
                            }

                            // Measurements
                            if (component.measurements) {
                                await this.add("measurements", component.measurements, {
                                    location_item_component_uuid: component.uuid,
                                });

                                for (const measurement of component.measurements) {
                                    if (measurement.images) {
                                        imageTasks.push(async () => {
                                            await this.add("images", measurement.images, {
                                                imageable_uuid: measurement.uuid,
                                            });
                                        });
                                    }
                                }
                            }

                            // Documents
                            if (component.documents) {
                                await this.add("documents", component.documents, { documentable_uuid: component.uuid });
                            }

                            // Answers
                            if (component.answers) {
                                await this.add("answers_location_item_components", component.answers, {
                                    location_item_component_uuid: component.uuid,
                                });
                            }

                            // Location systems
                            if (component.location_systems) {
                                await this.add("location_systems", component.location_systems, {
                                    location_systemable_uuid: component.uuid,
                                });
                            }
                        }
                    }
                });
            }

            // Then add all location_items in one bulk call
            nonImageTasks.push(async () => {
                await this.add("location_items", locationItems, { location_id: inspection.location.id });
            });
        }

        // Forms
        if (inspection.forms) {
            nonImageTasks.push(async () => {
                await this.add("forms", inspection.forms, { inspection_id: inspection.id }, "id");
            });

            // Form answers
            const answers = [];
            inspection.forms.forEach((form) => {
                if (form.answers) {
                    answers.push(...form.answers);
                }
            });

            if (answers.length > 0) {
                nonImageTasks.push(async () => {
                    await this.add("answers_inspections", answers, { inspection_id: inspection.id });
                });
            }
        }

        // To improve UI responsiveness, divide tasks into chunks and yield between processing

        // Execute non-image tasks (0% to 60%)
        const totalNonImageTasks = nonImageTasks.length;
        const nonImageTaskWeight = totalNonImageTasks > 0 ? 60 / totalNonImageTasks : 60;

        // Process non-image tasks in small batches
        const nonImageChunks = this.chunkArray(nonImageTasks, 5);

        for (let i = 0; i < nonImageChunks.length; i++) {
            const taskChunk = nonImageChunks[i];

            // Execute all tasks in this chunk
            await Promise.all(taskChunk.map((task) => task()));

            // Report progress
            progressCallback(
                (i + 1) * nonImageTaskWeight * (taskChunk.length / totalNonImageTasks),
                `Processing data chunk ${i + 1}/${nonImageChunks.length}`
            );

            // Yield to UI thread between chunks
            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        // Execute image tasks (60% to 90%)
        const totalImageTasks = imageTasks.length;
        const imageTaskWeight = totalImageTasks > 0 ? 30 / totalImageTasks : 30;

        // Process image tasks in small batches
        const imageChunks = this.chunkArray(imageTasks, 3); // Process fewer images at once

        for (let i = 0; i < imageChunks.length; i++) {
            const taskChunk = imageChunks[i];

            // Execute all tasks in this chunk
            await Promise.all(taskChunk.map((task) => task()));

            // Report progress
            progressCallback(
                60 + (i + 1) * imageTaskWeight * (taskChunk.length / totalImageTasks),
                `Processing images chunk ${i + 1}/${imageChunks.length}`
            );

            // Yield to UI thread between chunks - longer pause for image processing
            await new Promise((resolve) => setTimeout(resolve, 10));
        }

        // Finalize: parse and save inspection (90% to 100%)
        progressCallback(90, "Finalizing inspection data");
        const parsed_inspection = await module_parser("inspections", [inspection]);
        await db.inspections.bulkPut(parsed_inspection);

        progressCallback(100, "Inspection sync complete");
    }

    /**
     * Helper: Add or update items in the database
     */
    async add(module, payload, params, identifier_name = "uuid") {
        if (!payload) return;

        try {
            // First check if database is open
            if (!db.isOpen()) {
                console.warn(`Database not open for module ${module}, attempting to open`);
                try {
                    await db.open();
                    console.log(`Database opened for module ${module}`);
                } catch (dbError) {
                    console.error(`Failed to open database for module ${module}:`, dbError);
                    return; // Exit if we can't open the database
                }
            }

            // Validate the payload is an array
            if (!Array.isArray(payload)) {
                console.error(`Invalid payload for module ${module}: not an array`);
                return;
            }

            // Filter out items with invalid identifiers
            const validPayload = payload.filter((item) => {
                if (!item || typeof item !== "object") {
                    console.warn(`Skipping invalid item in ${module}: not an object`);
                    return false;
                }

                if (item[identifier_name] === undefined || item[identifier_name] === null) {
                    console.warn(`Skipping item with missing identifier ${identifier_name} in module ${module}`);
                    return false;
                }

                return true;
            });

            // Ensure all params are defined
            for (const key in params) {
                if (params[key] === undefined || params[key] === null) {
                    console.warn(
                        `Parameter ${key} is ${params[key]} for module ${module}, skipping database operation`
                    );
                    return;
                }
            }

            const localItems = await db[module]
                .where(params)
                .primaryKeys()
                .then((keys) => {
                    if (keys.length === 0) return [];
                    return db[module].where(identifier_name).anyOf(keys).toArray();
                })
                .catch((error) => {
                    console.error(`Error fetching local items for ${module}:`, error);
                    return []; // Return empty array on error
                });

            if (validPayload.length === 0) {
                if (localItems.length > 0) {
                    try {
                        await db[module]
                            .where(identifier_name)
                            .anyOf(localItems.map((item) => item[identifier_name]))
                            .delete();
                    } catch (deleteError) {
                        console.error(`Error deleting items from ${module}:`, deleteError);
                    }
                }
                return;
            }

            const localMap = Object.fromEntries(localItems.map((item) => [item[identifier_name], item]));
            const itemsToUpdateOrCreate = [];
            const identifiersToKeep = new Set();

            for (const remoteItem of validPayload) {
                // Ensure the item has all required properties
                if (!remoteItem.updated_at) {
                    console.warn(`Item in ${module} missing updated_at, using current time`);
                    remoteItem.updated_at = new Date().toISOString();
                }

                const localItem = localMap[remoteItem[identifier_name]];
                identifiersToKeep.add(remoteItem[identifier_name]);

                // Skip if item exists and updated_at is the same
                if (
                    localItem &&
                    new Date(remoteItem.updated_at).getTime() === new Date(localItem.updated_at).getTime()
                ) {
                    continue;
                }

                // Add for debugging: log when there's a timestamp mismatch causing redownload
                // Special handling for images to avoid re-downloading images we just uploaded
                if (module === "images" && localItem) {
                    // Backend's images.url field contains full S3 URL (DB stores path, API returns full URL)
                    // Extract S3 key from URL to compare with local s3_key
                    const extractS3KeyFromUrl = (url) => {
                        if (!url) return null;
                        try {
                            const urlObj = new URL(url);
                            // Remove leading slash and return the path (which is the S3 key)
                            return urlObj.pathname.replace(/^\//, "");
                        } catch (e) {
                            // If URL parsing fails, assume it's already just the S3 key
                            return url;
                        }
                    };

                    const localS3Key = localItem.s3_key || null;
                    const remoteS3Key = extractS3KeyFromUrl(remoteItem.url);

                    // Skip download if we already have this image synced
                    if (localItem.sync_status === "synced" && localS3Key && localItem.base64) {
                        // Case 1: S3 keys match - definitely the same image
                        // Case 2: Remote has no S3 key but we do and are synced - we just uploaded it
                        if (localS3Key === remoteS3Key || (!remoteS3Key && localS3Key)) {
                            console.log(`Skipping download for ${remoteItem.uuid} - already synced locally`);
                            // Still update metadata (in case other fields like title changed)
                            const metadataUpdate = { ...remoteItem };
                            delete metadataUpdate.url; // Don't need to download
                            metadataUpdate.base64 = localItem.base64; // Keep existing image data
                            metadataUpdate.s3_key = localS3Key; // Keep local S3 key
                            metadataUpdate.sync_status = 'synced'; // Maintain synced status
                            metadataUpdate.upload_status = metadataUpdate.upload_status || 'confirmed';
                            itemsToUpdateOrCreate.push(metadataUpdate);
                            continue;
                        }
                    }
                }

                // Add for update if remote is newer or item doesn't exist
                if (!localItem || new Date(remoteItem.updated_at) > new Date(localItem.updated_at)) {
                    if (module === "images") {
                        try {
                            // Log download with reason and S3 key details
                            let reason;
                            if (!localItem) {
                                reason = "new image";
                            } else {
                                // Extract S3 key from URL for comparison
                                const extractS3KeyFromUrl = (url) => {
                                    if (!url) return null;
                                    try {
                                        const urlObj = new URL(url);
                                        return urlObj.pathname.replace(/^\//, "");
                                    } catch (e) {
                                        return url;
                                    }
                                };

                                const localS3Key = localItem.s3_key || null;
                                const remoteS3Key = extractS3KeyFromUrl(remoteItem.url);

                                if (localS3Key !== remoteS3Key) {
                                    reason = "different S3 key";
                                    console.log(`  Local S3 key:  ${localS3Key ? `"${localS3Key}"` : "null"}`);
                                    console.log(`  Remote S3 key: ${remoteS3Key ? `"${remoteS3Key}"` : "null"}`);
                                } else {
                                    reason = "updated timestamp";
                                }
                            }
                            console.log(`Downloading image ${remoteItem.uuid}: ${reason}`);

                            // Fetch and convert image to base64
                            const blob = await this.fetchBlob(remoteItem.url);
                            remoteItem.base64 = blob ? await this.blobToBase64(blob) : null;
                            delete remoteItem.blob;

                            // Extract and store S3 key from URL for future comparisons
                            if (remoteItem.url && !remoteItem.s3_key) {
                                try {
                                    const urlObj = new URL(remoteItem.url);
                                    remoteItem.s3_key = urlObj.pathname.replace(/^\//, "");
                                } catch (e) {
                                    // If URL parsing fails, use url as-is (might already be S3 key)
                                    remoteItem.s3_key = remoteItem.url;
                                }
                            }

                            // Mark images from backend as synced (only if download succeeded)
                            if (remoteItem.base64) {
                                remoteItem.sync_status = 'synced';
                                remoteItem.upload_status = remoteItem.upload_status || 'confirmed';
                            }
                        } catch (imageError) {
                            console.error(`Error processing image for ${remoteItem[identifier_name]}:`, imageError);
                            // Still add the item even if image processing fails
                        }
                    }
                    itemsToUpdateOrCreate.push(remoteItem);
                }
            }

            // Delete local items not in remote data
            // For forms and answers, we should be more careful about deletion to avoid
            // accidentally deleting data from other inspections when syncing
            const shouldDeleteMissingItems = module !== "forms" && module !== "answers_inspections";

            const identifiersToDelete = localItems
                .filter((item) => !identifiersToKeep.has(item[identifier_name]))
                .map((item) => item[identifier_name]);

            if (identifiersToDelete.length > 0 && shouldDeleteMissingItems) {
                try {
                    await db[module].where(identifier_name).anyOf(identifiersToDelete).delete();
                } catch (deleteError) {
                    console.error(`Error deleting items from ${module}:`, deleteError);
                }
            }

            if (itemsToUpdateOrCreate.length > 0) {
                try {
                    // Ensure database is still open before writing
                    if (!db.isOpen()) {
                        await db.open();
                    }

                    if (module === "images") {
                        // For images, validate each item has the required uuid
                        const validImages = itemsToUpdateOrCreate.filter((item) => {
                            if (!item.uuid || !item.imageable_uuid) {
                                console.warn(`Skipping image with missing uuid or imageable_uuid`);
                                return false;
                            }
                            return true;
                        });

                        if (validImages.length > 0) {
                            await db[module].bulkPut(validImages);
                        }
                    } else {
                        const parsedPayload = await module_parser(module, itemsToUpdateOrCreate);

                        // Make sure parsedPayload is an array
                        if (!Array.isArray(parsedPayload)) {
                            console.error(`module_parser returned non-array for ${module}`);
                            return;
                        }

                        // Further validate that each item has the primary key
                        const validItems = parsedPayload.filter((item) => {
                            if (!item || typeof item !== "object") {
                                console.warn(`Invalid item in parsed payload for ${module}`);
                                return false;
                            }

                            if (item[identifier_name] === undefined || item[identifier_name] === null) {
                                console.warn(`Item missing ${identifier_name} in module ${module} after parsing`);
                                return false;
                            }

                            return true;
                        });

                        if (validItems.length > 0) {
                            await db[module].bulkPut(validItems);
                        }
                    }
                } catch (putError) {
                    console.error(`Error storing items in ${module}:`, putError);
                }
            }
        } catch (error) {
            console.error(`Unexpected error in add function for module ${module}:`, error);
        }
    }

    /**
     * Helper: Fetch a blob from a URL
     */
    async fetchBlob(url) {
        try {
            const response = await fetch(url, {
                mode: "cors",
                credentials: "omit",
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status}`);
            }

            return await response.blob();
        } catch (error) {
            console.error("Error fetching blob:", error);
            return null;
        }
    }

    /**
     * Helper: Convert a blob to base64
     */
    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Helper: Convert base64 to a File object
     */
    base64ToFile(base64Data, filename) {
        try {
            if (!base64Data) {
                throw new Error("Invalid base64 data: null or undefined");
            }

            const parts = base64Data.split(",");
            if (parts.length < 2) {
                throw new Error("Invalid base64 data format");
            }

            // Extract mime type from base64 string
            let mimeString = "image/jpeg"; // Default fallback
            const mimeMatch = parts[0].match(/data:(.*);base64/);
            if (mimeMatch && mimeMatch.length > 1) {
                mimeString = mimeMatch[1];
            }

            // Determine proper file extension based on mime type
            let extension = "jpg";
            if (mimeString === "image/png") extension = "png";
            if (mimeString === "image/gif") extension = "gif";
            if (mimeString === "image/webp") extension = "webp";

            // Create a more appropriate filename with the correct extension
            const fileNameWithExt = filename.includes(".") ? filename : `${filename}.${extension}`;

            const byteString = atob(parts[1]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);

            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([ia], { type: mimeString });
            return new File([blob], fileNameWithExt, { type: mimeString });
        } catch (error) {
            console.error("Error converting base64 to file:", error);
            throw error;
        }
    }

    /**
     * Helper: Split an array into chunks
     */
    chunkArray(array, chunkSize) {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }

    /**
     * Set up task timeout
     */
    setupTaskTimeout(duration = 5 * 60 * 1000) {
        // 5 minutes default
        clearTimeout(this.taskTimeoutId);

        this.taskTimeoutId = setTimeout(() => {
            if (!this.isRunning) return;

            const inactiveTime = Math.round((Date.now() - this.lastProgressUpdate) / 1000);
            console.error(`Task ${this.currentTask?.type} appears to be stalled (${inactiveTime}s without updates)`);

            // Task failed due to timeout
            this.isRunning = false;

            // Store task info before nullifying
            const taskType = this.currentTask?.type;
            const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

            // Reset state
            this.currentTask = null;

            // Trigger timeout callback
            this.triggerCallback("timeout", taskType, {
                inactiveSeconds: inactiveTime,
                duration: duration,
            });
        }, duration);
    }

    /**
     * Handle progress updates
     */
    handleProgress(taskType, progress, details) {
        // Reset task timeout on progress
        this.setupTaskTimeout();

        // Update timestamp for health monitoring
        this.lastProgressUpdate = Date.now();

        // Call the progress callback if registered
        this.triggerCallback("progress", taskType, { progress, details });

        // Update store based on task type
        switch (taskType) {
            case "process_queue":
                store.dispatch("setQueueProgress", progress);
                break;
            case "sync_inspection":
                store.dispatch("setInspectionSyncProgress", progress);
                break;
            case "compress_images":
                // For image compression, progress is tracked per call
                break;
        }
    }

    /**
     * Helper to call the appropriate callback if registered
     */
    triggerCallback(event, taskType, data) {
        // Task-specific callback
        if (this.callbacks[event]) {
            this.callbacks[event](data);
        }

        // Update store state based on task and event
        this.updateStoreState(taskType, event, data);
    }

    /**
     * Update Vuex store based on task events
     */
    updateStoreState(taskType, event, data) {
        switch (taskType) {
            case "process_queue":
                if (event === "start") {
                    store.dispatch("setQueue", true);
                } else if (["complete", "error", "stop", "timeout"].includes(event)) {
                    store.dispatch("setQueue", false);
                }
                break;

            case "sync_inspection":
                if (event === "start") {
                    store.dispatch("setSyncing", true);
                } else if (["complete", "error", "stop", "timeout"].includes(event)) {
                    store.dispatch("setSyncing", false);
                }

                // Special case: when sync is complete, update inspection sync time
                if (event === "complete") {
                    // Use setTimeout to ensure this runs after other updates
                    setTimeout(() => this.finalizeInspectionSync(), 500);
                }
                break;
        }
    }

    /**
     * Finalize inspection sync by updating last sync time
     */
    async finalizeInspectionSync() {
        try {
            const inspection_id = store.getters.getInspectionId;
            if (!inspection_id) {
                console.log("No inspection ID in store, skipping finalization");
                return;
            }

            const inspection = await db.inspections.get(inspection_id);

            if (inspection) {
                const inspection_sync = {
                    inspection_uuid: inspection.uuid,
                    updated_at: new Date().toISOString(),
                };

                store.dispatch("setInspectionSynced", inspection_sync);
            } else {
                console.log(`Inspection with ID ${inspection_id} not found in the database`);
            }
        } catch (error) {
            console.error("Error finalizing inspection sync:", error);
            // Don't rethrow - this shouldn't block the completion of sync
        }
    }

    /**
     * Setup base tables
     */
    async setupBaseTables(params, updateProgress) {
        const { api_url, bearer, online } = params;

        // Check network status - don't proceed if offline
        if (!online && !params.ignoreOffline) {
            throw new Error("Cannot set up base tables while offline");
        }

        if (!api_url || !bearer) {
            throw new Error("Missing API configuration for base tables setup");
        }

        updateProgress(0, "Starting base tables setup");

        try {
            // First check if database is open
            if (!db.isOpen()) {
                console.warn("Database not open for base tables setup, attempting to open");
                try {
                    await db.open();
                    console.log("Database opened for base tables setup");
                } catch (dbError) {
                    console.error("Failed to open database for base tables setup:", dbError);
                    throw new Error("Database access error: " + dbError.message);
                }
            }

            const tables = [
                "component_groups",
                "components",
                "danger_aspects",
                "error_classifications",
                "error_indications",
                "installation_types",
                "location_item_types",
                "measurement_types",
                "measurement_instrument_types",
                "measurement_instruments",
                "remarks",
                "repair_periods",
                "location_rotationfields",
                "inspection_method_types",
                "inspection_methods",
                "inspection_statuses",
                "location_system_types",
                "document_types",
            ];

            // Fetch all setup data in one request with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            try {
                const response = await fetch(`${api_url}/setup`, {
                    headers: {
                        Authorization: `Bearer ${bearer}`,
                        Accept: "application/vnd.api+json",
                        ContentType: "application/vnd.api+json",
                    },
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`Setup data fetch failed: ${response.status}`);
                }

                const data = await response.json();

                // Process each table - spread work evenly across progress 0-90%
                const progressPerTable = 90 / tables.length;

                // Process tables in batches for better UI responsiveness
                const tableChunks = this.chunkArray(tables, 4);

                for (let i = 0; i < tableChunks.length; i++) {
                    const tableChunk = tableChunks[i];

                    // Create a batch of promises for this chunk
                    const batchPromises = tableChunk.map(async (table) => {
                        const tableData = data[table];

                        if (tableData) {
                            try {
                                // Double-check database is still open
                                if (!db.isOpen()) {
                                    console.warn(`Database closed while processing table ${table}, reopening`);
                                    await db.open();
                                }

                                if (table === "components") {
                                    await db.base_components.bulkPut(tableData);
                                } else if (table === "measurement_types") {
                                    // Add (code) to the name for easier searching
                                    tableData.forEach((measurement_type) => {
                                        measurement_type.name = `${measurement_type.name} (${measurement_type.code})`;
                                    });
                                    await db.measurement_types.bulkPut(tableData);
                                } else {
                                    await db[table].bulkPut(tableData);
                                }

                                return { table, success: true };
                            } catch (tableError) {
                                console.error(`Error processing table ${table}:`, tableError);
                                return { table, success: false, error: tableError };
                            }
                        }
                        return { table, success: true, skipped: true };
                    });

                    // Wait for all tables in this chunk to process
                    const results = await Promise.all(batchPromises);

                    // Report progress
                    updateProgress(
                        progressPerTable * (i * tableChunk.length + tableChunk.length),
                        `Processed tables batch ${i + 1}/${tableChunks.length}`
                    );

                    // Yield to UI thread between chunks
                    await new Promise((resolve) => setTimeout(resolve, 0));
                }

                // Try to sync measurement type revisions
                updateProgress(90, "Syncing measurement type revisions");
                try {
                    await this.syncMeasurementTypeRevisions(api_url, bearer);
                } catch (revisionError) {
                    console.warn("Failed to sync measurement type revisions, continuing anyway:", revisionError);
                }

                updateProgress(100, "Base tables setup complete");
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === "AbortError") {
                    throw new Error("Setup data fetch timed out");
                }
                throw fetchError;
            }
        } catch (error) {
            console.error("Setup error:", error);
            throw error;
        }
    }

    /**
     * Sync measurement type revisions
     */
    async syncMeasurementTypeRevisions(api_url, bearer) {
        try {
            // First check if the database is open
            if (!db.isOpen()) {
                console.warn("Database not open for measurement type revisions sync, attempting to open");
                try {
                    await db.open();
                    console.log("Database opened for measurement type revisions");
                } catch (dbError) {
                    console.error("Failed to open database for measurement type revisions:", dbError);
                    throw new Error("Database access error: " + dbError.message);
                }
            }

            // Fetch the data with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await fetch(`${api_url}/measurement-type-revisions`, {
                headers: {
                    Authorization: `Bearer ${bearer}`,
                    Accept: "application/vnd.api+json",
                    ContentType: "application/vnd.api+json",
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Failed to fetch measurement type revisions: ${response.status}`);
            }

            const data = await response.json();

            // Check if data is valid
            if (!Array.isArray(data)) {
                console.warn("Measurement type revisions data is not an array, skipping");
                return; // Skip silently rather than failing
            }

            // Try to store the data with retry logic
            let stored = false;
            for (let attempt = 0; attempt < 3 && !stored; attempt++) {
                try {
                    // Double-check database is still open
                    if (!db.isOpen()) {
                        await db.open();
                    }

                    await db.measurement_type_revisions.bulkPut(data);
                    stored = true;
                    console.log(`Stored ${data.length} measurement type revisions on attempt ${attempt + 1}`);
                } catch (storageError) {
                    console.error(`Failed to store measurement type revisions (attempt ${attempt + 1}):`, storageError);

                    // Special handling for database closed errors
                    if (
                        storageError.name === "DatabaseClosedError" ||
                        storageError.message.includes("DatabaseClosedError")
                    ) {
                        console.log("Database closed during storage, attempting to reopen");
                        try {
                            await db.open();
                        } catch (reopenError) {
                            console.error("Error reopening database:", reopenError);
                        }
                    }

                    // Add delay before retry
                    if (attempt < 2) {
                        // Don't wait after last attempt
                        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
                    }
                }
            }

            if (!stored) {
                // Instead of failing hard, log the error but allow the process to continue
                console.error("Failed to store measurement type revisions after multiple attempts");
            }
        } catch (error) {
            console.error("Error in syncMeasurementTypeRevisions:", error);
            // Convert to a non-fatal error so the setup can complete
            console.warn("Continuing setup process despite measurement type revisions error");
        }
    }

    /**
     * Compress and save images
     */
    async compressImages(params, updateProgress) {
        const {
            images,
            upload_url,
            imageable_uuid,
            addTimestamp = false,
            addInspectorName = false,
            inspectorName = null,
            cropToSquare = false,
        } = params;

        // Validate input
        if (!images || !Array.isArray(images) || images.length === 0) {
            throw new Error("Invalid or empty images array");
        }

        updateProgress(0, `Starting compression of ${images.length} images`);

        // Process images in smaller batches for better UI responsiveness
        const imageChunks = this.chunkArray(images, 3); // Process 3 images at a time

        for (let i = 0; i < imageChunks.length; i++) {
            const imageChunk = imageChunks[i];

            // Process each image in this chunk sequentially (each image is heavy)
            for (let j = 0; j < imageChunk.length; j++) {
                const image = imageChunk[j];
                const imageIndex = i * 3 + j; // Calculate overall image index

                try {
                    // Validate base64 data
                    if (!image.base64 || typeof image.base64 !== "string") {
                        console.error(`Invalid base64 data for image ${imageIndex + 1}`);
                        continue;
                    }

                    updateProgress(
                        Math.round((imageIndex / images.length) * 50),
                        `Compressing image ${imageIndex + 1}/${images.length}`
                    );

                    // Compress the image (1MB target, quality 0.75)
                    // NOTE: convert.compress() ALWAYS outputs JPEG format regardless of input
                    // See convert.js documentation for rationale (backend compatibility, PDF generation, etc.)
                    const compressedBase64 = await convert.compress(
                        image.base64,
                        1, // maxSizeMB: 1MB (changed from 2MB)
                        1920, // targetWidth: max dimension
                        0.75, // minQuality: JPEG quality (changed from 0.9)
                        false, // addLabel: no label
                        cropToSquare,
                        addTimestamp,
                        addInspectorName,
                        inspectorName
                    );

                    // Create the new image object
                    // IMPORTANT: Always set format to 'image/jpeg' since convert.compress() always outputs JPEG
                    const newImage = {
                        ...image,
                        base64: compressedBase64,
                        compressed: true,
                        format: "image/jpeg", // Always JPEG after compression (was: format - bug!)
                    };

                    // Save to database with initial upload status
                    await db.images.put({
                        ...newImage,
                        upload_status: "pending",
                        sync_status: "pending",
                    });

                    // Create S3 upload job if URL is provided
                    if (upload_url) {
                        // Determine entity type from URL
                        let entityType = "observation"; // default
                        if (upload_url.includes("/measurements/")) {
                            entityType = "measurement";
                        } else if (upload_url.includes("/location-item-components/")) {
                            entityType = "location_item_component";
                        } else if (upload_url.includes("/locations/")) {
                            entityType = "location";
                        }

                        // If online (respects force offline mode), upload immediately
                        if (store.getters.online) {
                            try {
                                await imageUploadService.uploadImage(newImage, entityType, imageable_uuid);
                            } catch (error) {
                                console.error("Failed to upload image immediately:", error);
                                // Queue for later if immediate upload fails
                                const jobUuid = uuid();
                                await db.jobs.put({
                                    uuid: jobUuid,
                                    type: "image-s3-upload",
                                    module: "images",
                                    url: null,
                                    attempt: 0, // Initialize attempt counter
                                    payload: {
                                        uuid: jobUuid,
                                        image_uuid: newImage.uuid,
                                        imageable_uuid: imageable_uuid,
                                        entity_type: entityType,
                                    },
                                    created_at: new Date().toISOString(),
                                });
                            }
                        } else {
                            // Queue for later upload when offline
                            const jobUuid = uuid();
                            await db.jobs.put({
                                uuid: jobUuid,
                                type: "image-s3-upload",
                                module: "images",
                                url: null,
                                attempt: 0, // Initialize attempt counter
                                payload: {
                                    uuid: jobUuid,
                                    image_uuid: newImage.uuid,
                                    imageable_uuid: imageable_uuid,
                                    entity_type: entityType,
                                },
                                created_at: new Date().toISOString(),
                            });
                        }
                    }

                    // Update progress for second half (saving)
                    updateProgress(
                        50 + Math.round((imageIndex / images.length) * 50),
                        `Saving image ${imageIndex + 1}/${images.length}`
                    );
                } catch (error) {
                    console.error(`Error processing image ${imageIndex + 1}:`, error);
                }
            }

            // Yield to UI thread between chunks to prevent freezing
            await new Promise((resolve) => setTimeout(resolve, 10));
        }

        updateProgress(100, `Successfully processed ${images.length} images`);
    }

    /**
     * Stop the current task
     */
    stopTask(reason = "User requested stop") {
        if (!this.isRunning) {
            console.log("No task running to stop");
            return false;
        }

        // Store task info before stopping
        const taskType = this.currentTask?.type;
        const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

        // Update state
        this.isRunning = false;
        this.currentTask = null;

        // Clear timeout
        clearTimeout(this.taskTimeoutId);

        // Trigger stop callback
        this.triggerCallback("stop", taskType, {
            reason,
            duration,
        });

        return true;
    }

    /**
     * Check status of current task
     */
    checkStatus() {
        return {
            running: this.isRunning,
            task: this.currentTask,
            lastUpdate: this.lastProgressUpdate,
            platform: this.platform,
        };
    }
}

// Export singleton instance
export default new SyncManager();
